/**
 * VerificationTierPanel — surfaces the Verification Tier Capping
 * impact for a Company. Sits next to ResearchSourcesPanel on the
 * detail page so reviewers can see *what the sources are verifying*,
 * not just *what sources exist*.
 *
 * Lazy-loaded: clicking "Run scoring with tier caps" hits
 * POST /scoring-engine/companies/{id}/score which auto-derives the
 * verification_tiers map from the company's research_sources +
 * ein_verified flags. Doesn't perturb the existing rubric assessment.
 *
 * Display:
 *   - Tier-cap legend (Self-Reported = cap 3, Partially Verified = cap 4)
 *   - Per-criterion table: criterion → tier badge → capped amount
 *     (red when raw > cap, gray "no cap" otherwise)
 *   - Per-category before/after percentage bars
 *   - Top-line: "X criteria elevated to PARTIALLY_VERIFIED via
 *     {linkedin, github, ...}"
 */
import React, { useState } from 'react';
import {
  Shield, ShieldCheck, ShieldAlert, Loader2, ChevronDown,
} from 'lucide-react';
import api from '../services/api';

interface Props {
  companyId: number;
}

// VerificationTier int enum values (mirrors backend feedback.VerificationTier)
const TIER = {
  UNVERIFIED: 0,
  SELF_REPORTED: 1,
  PARTIALLY_VERIFIED: 2,
  ADMIN_VERIFIED: 3,
} as const;

const TIER_META: Record<number, { label: string; cap: number; color: string; bg: string }> = {
  [TIER.UNVERIFIED]: { label: 'Unverified', cap: 5, color: 'text-gray-700', bg: 'bg-gray-100' },
  [TIER.SELF_REPORTED]: { label: 'Self-reported', cap: 3, color: 'text-amber-800', bg: 'bg-amber-50' },
  [TIER.PARTIALLY_VERIFIED]: { label: 'Partially verified', cap: 4, color: 'text-blue-800', bg: 'bg-blue-50' },
  [TIER.ADMIN_VERIFIED]: { label: 'Admin verified', cap: 5, color: 'text-emerald-800', bg: 'bg-emerald-50' },
};

interface CriterionImpact {
  raw: number;
  capped: number;
  tier: number;
  capped_amount: number;
}

interface ScoreResponse {
  verification_tiers_used?: Record<string, number>;
  tier_capped?: {
    capped_rubric_scores?: Record<string, Record<string, number>>;
    tier_impact?: Record<string, Record<string, CriterionImpact>>;
    category_percentages?: Record<string, number>;
    total_capped_score?: number;
  };
  total?: { total_score?: number };
}

const formatCriterion = (id: string): string => {
  return id
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

const VerificationTierPanel: React.FC<Props> = ({ companyId }) => {
  const [data, setData] = useState<ScoreResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  const runScoring = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await api.post<ScoreResponse>(
        `/scoring-engine/companies/${companyId}/score`,
      );
      setData(resp.data);
      setExpanded(true);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to run scoring');
    } finally {
      setLoading(false);
    }
  };

  // Derive top-line summary stats from data.
  const tiers = data?.verification_tiers_used || {};
  const tierEntries = Object.entries(tiers);
  const partiallyVerified = tierEntries.filter(
    ([, t]) => t === TIER.PARTIALLY_VERIFIED,
  );
  const adminVerified = tierEntries.filter(
    ([, t]) => t === TIER.ADMIN_VERIFIED,
  );

  const tierImpact = data?.tier_capped?.tier_impact || {};
  const cappedItems: Array<{ category: string; criterion: string; impact: CriterionImpact }> = [];
  Object.entries(tierImpact).forEach(([cat, items]) => {
    Object.entries(items || {}).forEach(([crit, impact]) => {
      if (impact && impact.capped_amount > 0) {
        cappedItems.push({ category: cat, criterion: crit, impact });
      }
    });
  });

  const beforeTotal = data?.total?.total_score;
  const afterTotal = data?.tier_capped?.total_capped_score;
  const totalDelta =
    typeof beforeTotal === 'number' && typeof afterTotal === 'number'
      ? Math.round((beforeTotal - afterTotal) * 10) / 10
      : null;

  return (
    <section className="bg-white border border-gray-200 rounded-lg p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-gray-400" />
          Verification tier impact
        </h2>
        {data && (
          <button
            type="button"
            onClick={() => setExpanded((e) => !e)}
            className="text-xs text-gray-500 hover:text-gray-700 inline-flex items-center gap-1"
          >
            <ChevronDown
              className={`w-3 h-3 transition-transform ${expanded ? 'rotate-180' : ''}`}
            />
            {expanded ? 'Collapse' : 'Expand'}
          </button>
        )}
      </div>

      <p className="text-xs text-gray-600 mb-3">
        Anti-hype caps applied per criterion based on which sources verify
        the claim. Self-reported claims cap at 3/5; sources like LinkedIn,
        GitHub, or SEC EDGAR elevate to partially verified (cap 4/5).
      </p>

      {!data && (
        <button
          type="button"
          onClick={runScoring}
          disabled={loading}
          className="inline-flex items-center px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg disabled:opacity-50"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
          ) : (
            <Shield className="w-4 h-4 mr-1.5" />
          )}
          {loading ? 'Running...' : 'Run scoring with tier caps'}
        </button>
      )}

      {error && (
        <div className="text-sm text-red-600 mt-2">{error}</div>
      )}

      {data && (
        <>
          {/* Top-line summary */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2 mb-4">
            <div className="bg-gray-50 rounded p-2">
              <div className="text-[10px] text-gray-500 uppercase">Total criteria</div>
              <div className="text-base font-semibold text-gray-900">{tierEntries.length}</div>
            </div>
            <div className="bg-blue-50 rounded p-2">
              <div className="text-[10px] text-blue-600 uppercase">Partially verified</div>
              <div className="text-base font-semibold text-blue-900">{partiallyVerified.length}</div>
            </div>
            <div className="bg-emerald-50 rounded p-2">
              <div className="text-[10px] text-emerald-600 uppercase">Admin verified</div>
              <div className="text-base font-semibold text-emerald-900">{adminVerified.length}</div>
            </div>
            <div className={`rounded p-2 ${totalDelta && totalDelta > 0 ? 'bg-red-50' : 'bg-gray-50'}`}>
              <div className={`text-[10px] uppercase ${totalDelta && totalDelta > 0 ? 'text-red-600' : 'text-gray-500'}`}>
                Score impact
              </div>
              <div className={`text-base font-semibold ${totalDelta && totalDelta > 0 ? 'text-red-700' : 'text-gray-900'}`}>
                {totalDelta && totalDelta > 0
                  ? `−${totalDelta}`
                  : totalDelta === 0
                    ? 'no change'
                    : '—'}
              </div>
            </div>
          </div>

          {expanded && (
            <>
              {/* Tier legend */}
              <div className="flex flex-wrap gap-2 mb-3">
                {[TIER.SELF_REPORTED, TIER.PARTIALLY_VERIFIED, TIER.ADMIN_VERIFIED].map((t) => {
                  const meta = TIER_META[t];
                  return (
                    <span key={t} className={`text-[10px] px-2 py-0.5 rounded ${meta.bg} ${meta.color} font-medium`}>
                      {meta.label} (cap {meta.cap})
                    </span>
                  );
                })}
              </div>

              {/* Capped items table */}
              {cappedItems.length > 0 ? (
                <div className="border border-gray-100 rounded mb-3">
                  <div className="px-3 py-2 bg-gray-50 text-[10px] font-medium text-gray-600 uppercase tracking-wide">
                    Criteria capped by tier ({cappedItems.length})
                  </div>
                  <ul className="divide-y divide-gray-100">
                    {cappedItems.map((row) => {
                      const meta = TIER_META[row.impact.tier];
                      return (
                        <li key={`${row.category}.${row.criterion}`} className="px-3 py-2 flex items-center justify-between">
                          <div className="min-w-0 flex-1">
                            <div className="text-sm text-gray-900 truncate">
                              {formatCriterion(row.criterion)}
                            </div>
                            <div className="text-[10px] text-gray-500">
                              {row.category}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className="text-xs text-gray-500">
                              {row.impact.raw}/5 → <strong className="text-red-700">{row.impact.capped}/5</strong>
                            </span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded ${meta.bg} ${meta.color} font-medium`}>
                              {meta.label}
                            </span>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : (
                <div className="text-xs text-gray-500 mt-2 mb-3 flex items-center gap-1.5">
                  <ShieldAlert className="w-3 h-3" />
                  No criteria were capped — all raw scores fell within their tier limits.
                </div>
              )}

              {/* All criteria with their tier */}
              <details className="border border-gray-100 rounded">
                <summary className="px-3 py-2 bg-gray-50 text-[10px] font-medium text-gray-600 uppercase tracking-wide cursor-pointer">
                  All {tierEntries.length} criteria + tier
                </summary>
                <ul className="divide-y divide-gray-100">
                  {tierEntries.map(([crit, t]) => {
                    const meta = TIER_META[t];
                    return (
                      <li key={crit} className="px-3 py-1.5 flex items-center justify-between">
                        <span className="text-sm text-gray-800">{formatCriterion(crit)}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${meta.bg} ${meta.color} font-medium`}>
                          {meta.label}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </details>

              <div className="mt-3 pt-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={runScoring}
                  disabled={loading}
                  className="text-xs text-blue-600 hover:text-blue-700 inline-flex items-center gap-1 disabled:opacity-50"
                >
                  {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                  Re-run scoring
                </button>
              </div>
            </>
          )}
        </>
      )}
    </section>
  );
};

export default VerificationTierPanel;
